import { ReactNode } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin } from "lucide-react";
import { JobCardPhoto } from "@/components/JobCardPhoto";

/* ── helpers ─────────────────────────────────────────────── */

export const getUnifiedStatusColor = (status: string) => {
  switch (status) {
    case "pending": return "bg-yellow-100 text-yellow-800";
    case "picked": return "bg-blue-100 text-blue-800";
    case "quoted": return "bg-purple-100 text-purple-800";
    case "quote_accepted":
    case "payment_pending": return "bg-orange-100 text-orange-800";
    case "assigned": return "bg-sky-100 text-sky-800";
    case "in_progress": return "bg-indigo-100 text-indigo-800";
    case "completed": return "bg-emerald-100 text-emerald-800";
    case "cancelled":
    case "canceled": return "bg-red-100 text-red-800";
    default: return "bg-muted text-muted-foreground";
  }
};

export const getUnifiedPriorityColor = (priority: string) => {
  switch (priority) {
    case "emergency": return "bg-red-100 text-red-800";
    case "urgent": return "bg-orange-100 text-orange-800";
    case "standard":
    default: return "bg-muted text-muted-foreground";
  }
};

const statusBorderColor = (status: string) => {
  switch (status) {
    case "pending": return "border-l-yellow-400";
    case "picked": return "border-l-blue-400";
    case "quoted": return "border-l-purple-400";
    case "quote_accepted":
    case "payment_pending": return "border-l-orange-400";
    case "assigned": return "border-l-sky-400";
    case "in_progress": return "border-l-indigo-500";
    case "completed": return "border-l-emerald-500";
    case "cancelled":
    case "canceled": return "border-l-red-400";
    default: return "border-l-border";
  }
};

/* ── Card Shell ──────────────────────────────────────────── */

interface JobCardShellProps {
  status: string;
  children: ReactNode;
  onClick?: () => void;
  className?: string;
  highlight?: boolean;
}

export function JobCardShell({ status, children, onClick, className = "", highlight = false }: JobCardShellProps) {
  return (
    <Card
      className={`
        border-l-4 ${statusBorderColor(status)}
        bg-card shadow-md hover:shadow-lg transition-all duration-300
        ${highlight ? "ring-2 ring-yellow-400 shadow-yellow-200/50" : ""}
        ${onClick ? "cursor-pointer" : ""}
        ${className}
      `}
      onClick={onClick}
    >
      {children}
    </Card>
  );
}

/* ── Card Header ─────────────────────────────────────────── */

interface JobCardHeaderProps {
  jobId: string;
  title: string;
  location: string;
  status: string;
  statusLabel?: string;
  priority?: string;
  jobCategory?: string | null;
  jobNumber?: number;
  distance?: number | string;
  showPhoto?: boolean;
  extraBadges?: ReactNode;
  topRight?: ReactNode;
}

export function JobCardHeaderSection({
  jobId,
  title,
  location,
  status,
  statusLabel,
  priority,
  jobCategory,
  jobNumber,
  distance,
  showPhoto = true,
  extraBadges,
  topRight,
}: JobCardHeaderProps) {
  return (
    <CardHeader className="pb-3">
      {showPhoto && <JobCardPhoto jobId={jobId} className="h-44 w-full mb-3 rounded-md" />}
      <div className="flex justify-between items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="text-lg font-bold text-foreground leading-tight">{title}</h3>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0">
              {jobNumber ? `#${jobNumber}` : `ID: ${jobId.slice(0, 8)}`}
            </Badge>
            {jobCategory && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                {jobCategory}
              </Badge>
            )}
            {extraBadges}
          </div>
          <p className="flex items-center gap-1 text-sm text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">{location}</span>
            {distance && Number(distance) > 0 && (
              <span className="ml-1 text-xs whitespace-nowrap">({distance} mi)</span>
            )}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <Badge className={`${getUnifiedStatusColor(status)} text-xs shadow-sm`} variant="secondary">
            {statusLabel || status.replace("_", " ")}
          </Badge>
          {priority && priority !== "standard" && (
            <Badge className={`${getUnifiedPriorityColor(priority)} text-xs`} variant="secondary">
              {priority}
            </Badge>
          )}
          {topRight}
        </div>
      </div>
    </CardHeader>
  );
}

/* ── Divider ─────────────────────────────────────────────── */

export function JobCardDivider() {
  return (
    <div className="px-5">
      <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
    </div>
  );
}

/* ── Content Wrapper ─────────────────────────────────────── */

export function JobCardBody({ children }: { children: ReactNode }) {
  return <CardContent className="pt-3 pb-4 space-y-4">{children}</CardContent>;
}

/* ── Description ─────────────────────────────────────────── */

export function JobCardDescription({ text }: { text: string }) {
  return (
    <p className="text-sm text-foreground/80 line-clamp-3 leading-relaxed">
      {text}
    </p>
  );
}

/* ── Info Row ────────────────────────────────────────────── */

interface InfoRowProps {
  label: string;
  value: ReactNode;
  variant?: "default" | "success" | "muted" | "accent";
}

const variantBg: Record<string, string> = {
  default: "bg-muted/40",
  success: "bg-emerald-50",
  muted: "bg-muted/30",
  accent: "bg-primary/5",
};

export function JobCardInfoRow({ label, value, variant = "default" }: InfoRowProps) {
  return (
    <div className={`flex justify-between items-center px-3 py-2 rounded-lg ${variantBg[variant]}`}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold text-foreground">{value}</span>
    </div>
  );
}

/* ── Info Section ────────────────────────────────────────── */

export function JobCardInfoSection({ children }: { children: ReactNode }) {
  return <div className="space-y-1.5">{children}</div>;
}

/* ── Actions ─────────────────────────────────────────────── */

export function JobCardActions({ children }: { children: ReactNode }) {
  return <div className="space-y-2">{children}</div>;
}

export function JobCardActionRow({ children }: { children: ReactNode }) {
  return <div className="flex gap-2">{children}</div>;
}

/* ── Consistent Button Styles ────────────────────────────── */

interface JobCardButtonProps {
  onClick: (e?: React.MouseEvent) => void;
  icon?: ReactNode;
  label: string;
  variant?: "default" | "outline" | "destructive" | "hero" | "success";
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
}

export function JobCardButton({
  onClick,
  icon,
  label,
  variant = "outline",
  fullWidth = true,
  disabled = false,
  className = "",
}: JobCardButtonProps) {
  const baseClass = `${fullWidth ? "w-full" : "flex-1"} shadow-sm hover:shadow-md transition-all duration-200 text-sm`;

  // Map custom variants to Button variants
  const buttonVariant = variant === "hero" ? "default" : variant === "success" ? "default" : variant;
  const extraClass =
    variant === "hero"
      ? "bg-gradient-to-r from-primary to-primary/80 text-primary-foreground font-medium"
      : variant === "success"
      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
      : "";

  return (
    <Button
      variant={buttonVariant as any}
      size="sm"
      className={`${baseClass} ${extraClass} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      disabled={disabled}
    >
      {icon && <span className="mr-1.5">{icon}</span>}
      {label}
    </Button>
  );
}
